import { useCounter } from '../hooks/useCounter'

// Animated Stat Component
const AnimatedStat = ({ value, label, description, color }: { 
  value: string; 
  label: string; 
  description: string; 
  color: string 
}) => {
  // Handle different formats: "24/7", "90%", "10x"
  let numericValue = 0
  let displayValue = value

  if (value.includes('/')) {
    // Handle "24/7" format
    const nums = value.split('/')
    numericValue = parseInt(nums[0])
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}/${nums[1]}`
    
    return (
      <div ref={ref} className="stat-item">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
        <p className="text-gray-600">{description}</p>
      </div>
    )
  } else if (value.includes('%')) {
    // Handle "90%" format
    numericValue = parseInt(value.replace('%', ''))
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}%`
    
    return (
      <div ref={ref} className="stat-item">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
        <p className="text-gray-600">{description}</p>
      </div>
    )
  } else if (value.includes('x')) {
    // Handle "10x" format
    numericValue = parseInt(value.replace('x', ''))
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}x`
    
    return (
      <div ref={ref} className="stat-item">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
        <p className="text-gray-600">{description}</p>
      </div>
    )
  }

  // Fallback for other formats
  return (
    <div className="stat-item">
      <div className={`text-4xl font-bold ${color} mb-2`}>{value}</div>
      <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}

const Introduction = () => {
  return (
    <section id="Introduction" className="section introduction py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Experience That Makes{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                the Difference
              </span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              AI agents have enormous potential, but turning that potential into production reality requires expertise.
              We've spent years helping companies navigate the complexity—from initial strategy to monetizable products.
              Whether you're exploring possibilities or optimizing existing systems, we bring proven methodologies and hard-won insights.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <AnimatedStat
              value="24/7"
              label="Uptime"
              description="Production-grade AI systems built to last and deliver consistent results."
              color="text-blue-600"
            />
            <AnimatedStat
              value="90%"
              label="Cost Reduction"
              description="Typical efficiency gains from well-implemented AI workflows."
              color="text-purple-600"
            />
            <AnimatedStat
              value="10x"
              label="Production"
              description="Speed improvements with properly designed agentic systems."
              color="text-indigo-600"
            />
          </div>

          {/* Key Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Strategic Guidance */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/autonomous-decision.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Strategic Guidance</h3>
                <p className="text-white">
                  Navigate the complexity of AI implementation with expert consultants who've guided Fortune 500 companies through successful deployments for years.
                </p>
              </div>
            </div>

            {/* Production-Ready Development */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/continuous-learning.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Production-Ready Development</h3>
                <p className="text-white">
                  Build AI workflows that work in the real world—not just demos. We help you create robust, reliable systems that deliver measurable business value.
                </p>
              </div>
            </div>

            {/* Secure Validation & Testing */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/error-free-execution.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Secure Validation & Testing</h3>
                <p className="text-white">
                  Our proprietary validation tools use internal open-source LLMs to thoroughly test AI systems—keeping your data secure on your infrastructure.
                </p>
              </div>
            </div>

            {/* Proven Methodologies */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/infinite-scalability.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Proven Methodologies</h3>
                <p className="text-white">
                  Leverage battle-tested approaches that work. We've seen what succeeds and what fails—helping you avoid costly mistakes and accelerate time-to-value.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Introduction
